/**
 * Typography Design Tokens
 */

export const fontFamilies = {
  primary: 'var(--font-primary)',
  secondary: 'var(--font-secondary)',
  display: 'var(--font-display)',
  text: 'var(--font-text)',
  button: 'var(--font-button)',
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  bold: 700,
} as const;

export const fontSizes = {
  // Display sizes
  bodyTitle: '1.25rem', // 20px
  cardTitle: '1.75rem', // 28px
  pageTitle: '3.5rem', // 56px
  sectionSubTitle: '1.25rem', // 20px
  sectionTitle: '1.75rem', // 28px
  
  // Body sizes
  bodyLarge: '1.25rem', // 20px
  body: '1.00rem', // 16px
  bodySmall: '0.875rem', // 14px
  
  // Component sizes
  button: '1.125rem', // 18px (desktop/tablet)
  buttonLarge: '1.125rem', // 18px
  buttonMobile: '0.875rem', // 14px
  buttonSmall: '0.875rem', // 14px
  label: '1.25rem', // 20px
  tag: '0.875rem', // 14px
} as const;

export const lineHeights = {
  // Display line heights
  bodyTitle: '1.5rem', // 24px
  cardTitle: '2rem', // 32px
  pageTitle: '4rem', // 64px
  sectionSubTitle: '1.5rem', // 24px
  sectionTitle: '2rem', // 32px
  
  // Body line heights
  bodyLarge: '1.75rem', // 28px
  body: '1.25rem', // 20px
  bodySmall: '1.25rem', // 20px (based on 19px rounded)
  
  // Component line heights
  button: '2rem', // 32px
  buttonLarge: '2rem', // 32px
  buttonSmall: '1.75rem', // 28px
  label: '1.625rem', // 26px
  tag: '1.75rem', // 28px
} as const;

/**
 * Typography variants for consistent text styling across the application
 */
export const typography = {
  // Display styles
  pageTitle: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.pageTitle,
    lineHeight: lineHeights.pageTitle,
  },
  sectionTitle: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.sectionTitle,
    lineHeight: lineHeights.sectionTitle,
  },
  sectionSubTitle: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.sectionSubTitle,
    lineHeight: lineHeights.sectionSubTitle,
  },
  cardTitle: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.cardTitle,
    lineHeight: lineHeights.cardTitle,
  },
  bodyTitle: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.bodyTitle,
    lineHeight: lineHeights.bodyTitle,
  },
  
  // Body styles
  bodyLarge: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.regular,
    fontSize: fontSizes.bodyLarge,
    lineHeight: lineHeights.bodyLarge,
  },
  bodyMedium: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.regular,
    fontSize: fontSizes.body,
    lineHeight: lineHeights.body,
  },
  bodySmall: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.regular,
    fontSize: fontSizes.bodySmall,
    lineHeight: lineHeights.bodySmall,
  },
  
  // Component styles
  tag: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.tag,
    lineHeight: lineHeights.tag,
  },
  label: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.medium,
    fontSize: fontSizes.label,
    lineHeight: lineHeights.label,
  },
  buttonSmall: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.buttonSmall,
    lineHeight: lineHeights.buttonSmall,
  },
  buttonLarge: {
    fontFamily: fontFamilies.text,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.buttonLarge,
    lineHeight: lineHeights.buttonLarge,
  },
  button: {
    fontFamily: fontFamilies.button,
    fontWeight: fontWeights.bold,
    fontSize: fontSizes.button,
    lineHeight: lineHeights.button,
  },
} as const;

export type TypographyVariant = keyof typeof typography;
export type FontFamily = typeof fontFamilies[keyof typeof fontFamilies];
export type FontWeight = typeof fontWeights[keyof typeof fontWeights];
export type FontSize = typeof fontSizes[keyof typeof fontSizes];
export type LineHeight = typeof lineHeights[keyof typeof lineHeights];
